import { useEffect, useRef, RefObject } from "react";

type EventType = keyof DocumentEventMap;

export function useEventChangeOutside<T extends HTMLElement>(
  handler: () => void,
  eventType: EventType
): RefObject<T> {
  const domNode = useRef<T>(null);

  useEffect(() => {
    const eventHandler = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("[data-toolbar-portal]")) {
        return;
      }
      if (domNode.current && !domNode.current.contains(event.target as Node)) {
        handler();
      }
    };
    document.addEventListener(eventType, eventHandler);
    return () => {
      document.removeEventListener(eventType, eventHandler);
    };
  }, [eventType, handler]);

  return domNode;
}

export function useClickOutsideWithPortal<T extends HTMLElement>(
  handler: () => void,
  eventType: EventType,
  portalRef?: RefObject<HTMLElement>
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const listener = (e: Event) => {
      const inMain = ref.current?.contains(e.target as Node) ?? false;
      const inPortal = portalRef?.current?.contains(e.target as Node) ?? false;
      if (!inMain && !inPortal) handler();
    };
    document.addEventListener(eventType, listener);
    return () => document.removeEventListener(eventType, listener);
  }, [eventType, handler, portalRef]);

  return ref;
}
